import React, { useState } from 'react';
import './CategoryModal.css';
import { DeleteCategoryModal } from './DeleteCategoryModal';
import { TbArrowUp, TbArrowDown } from 'react-icons/tb';

interface Category {
    id: string;
    name: string;
    order: number;
    archived?: boolean;
}

interface CategoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    categories: Category[];
    archivedCategories: Category[];
    onEditCategory: (category: Category, newName: string) => void;
    onDeleteCategory: (category: Category) => void;
    onArchiveCategory: (category: Category) => void;
    onUnarchiveCategory: (category: Category) => void;
    onReorderCategories: (categories: Category[]) => void;
}

export const CategoryModal: React.FC<CategoryModalProps> = ({
    isOpen,
    onClose,
    categories,
    archivedCategories,
    onEditCategory,
    onDeleteCategory,
    onArchiveCategory,
    onUnarchiveCategory,
    onReorderCategories,
}) => {
    const [editingCategory, setEditingCategory] = useState<Category | null>(null);
    const [newName, setNewName] = useState('');
    const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
    const [showArchived, setShowArchived] = useState(false);

    if (!isOpen) return null;

    const handleEdit = (category: Category) => {
        setEditingCategory(category);
        setNewName(category.name);
    };

    const handleSave = () => {
        if (editingCategory && newName.trim()) {
            onEditCategory(editingCategory, newName.trim());
            setEditingCategory(null);
            setNewName('');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            setEditingCategory(null);
            setNewName('');
        }
    };

    const handleDeleteClick = (category: Category) => {
        setCategoryToDelete(category);
    };

    const handleDeleteConfirm = () => {
        if (categoryToDelete) {
            onDeleteCategory(categoryToDelete);
            setCategoryToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setCategoryToDelete(null);
    };

    const handleMoveUp = (category: Category) => {
        const currentIndex = categories.findIndex(c => c.id === category.id);
        if (currentIndex > 0) {
            const newCategories = [...categories];
            const temp = newCategories[currentIndex];
            newCategories[currentIndex] = newCategories[currentIndex - 1];
            newCategories[currentIndex - 1] = temp;

            // Add transition class to both items being swapped
            const currentElement = document.querySelector(`[data-category-id="${category.id}"]`);
            const prevElement = document.querySelector(`[data-category-id="${newCategories[currentIndex].id}"]`);
            if (currentElement && prevElement) {
                currentElement.classList.add('transitioning');
                prevElement.classList.add('transitioning');
                setTimeout(() => {
                    currentElement.classList.remove('transitioning');
                    prevElement.classList.remove('transitioning');
                }, 300); // Match this with CSS transition duration
            }

            onReorderCategories(newCategories);
        }
    };

    const handleMoveDown = (category: Category) => {
        const currentIndex = categories.findIndex(c => c.id === category.id);
        if (currentIndex < categories.length - 1) {
            const newCategories = [...categories];
            const temp = newCategories[currentIndex];
            newCategories[currentIndex] = newCategories[currentIndex + 1];
            newCategories[currentIndex + 1] = temp;

            // Add transition class to both items being swapped
            const currentElement = document.querySelector(`[data-category-id="${category.id}"]`);
            const nextElement = document.querySelector(`[data-category-id="${newCategories[currentIndex].id}"]`);
            if (currentElement && nextElement) {
                currentElement.classList.add('transitioning');
                nextElement.classList.add('transitioning');
                setTimeout(() => {
                    currentElement.classList.remove('transitioning');
                    nextElement.classList.remove('transitioning');
                }, 300); // Match this with CSS transition duration
            }

            onReorderCategories(newCategories);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content category-modal" onClick={e => e.stopPropagation()}>
                <h2>Categories</h2>
                <p className="category-subtext">Ranked in order of priority</p>
                <div className="category-list">
                    {categories.map((category, index) => (
                        <div
                            key={category.id}
                            data-category-id={category.id}
                            className="category-item"
                        >
                            {editingCategory?.id === category.id ? (
                                <input
                                    type="text"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    onBlur={handleSave}
                                    autoFocus
                                />
                            ) : (
                                <>
                                    <div className="reorder-buttons">
                                        <button
                                            className="reorder-btn up"
                                            onClick={() => handleMoveUp(category)}
                                            disabled={index === 0}
                                        >
                                            <TbArrowUp />
                                        </button>
                                        <button
                                            className="reorder-btn down"
                                            onClick={() => handleMoveDown(category)}
                                            disabled={index === categories.length - 1}
                                        >
                                            <TbArrowDown />
                                        </button>
                                    </div>
                                    <span className="category-number">{index + 1}.</span>
                                    <span className="category-name">{category.name}</span>
                                    <div className="category-actions">
                                        <button
                                            className="edit-btn"
                                            onClick={() => handleEdit(category)}
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            className="archive-btn"
                                            onClick={() => onArchiveCategory(category)}
                                            title="Archive category"
                                        >
                                            📦
                                        </button>
                                        <button
                                            className="delete-btn"
                                            onClick={() => handleDeleteClick(category)}
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
                
                {archivedCategories.length > 0 && (
                    <div className="archived-section">
                        <button
                            className="show-archived-btn"
                            onClick={() => setShowArchived(!showArchived)}
                        >
                            {showArchived ? '▼' : '▶'} View Archived Categories ({archivedCategories.length})
                        </button>
                        
                        {showArchived && (
                            <div className="archived-categories">
                                {archivedCategories.map((category, index) => (
                                    <div
                                        key={category.id}
                                        className="category-item archived"
                                    >
                                        <span className="category-number archived">{index + 1}.</span>
                                        <span className="category-name archived">{category.name}</span>
                                        <div className="category-actions">
                                            <button
                                                className="unarchive-btn"
                                                onClick={() => onUnarchiveCategory(category)}
                                                title="Unarchive category"
                                            >
                                                📤
                                            </button>
                                            <button
                                                className="delete-btn"
                                                onClick={() => handleDeleteClick(category)}
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <DeleteCategoryModal
                isOpen={categoryToDelete !== null}
                categoryName={categoryToDelete?.name || ''}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            />
        </div>
    );
}; 